import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import type { StreamChat } from "stream-chat";
import {
  getChatPushPreference,
  registerChatPushDevice,
  requestChatPushPermission,
  setChatPushPreference,
} from "Infrastructure/Capacitor/chatPushDevice";
import { isNativePlatform } from "Utils/Platform/PlatformUtils";

/**
 * The prompt in flight, if any. The preference is only written once the user answers, so a second
 * effect run before that (StrictMode double-invoke, a remount) must join the pending prompt
 * instead of asking the OS again and registering the device twice.
 */
let inFlightPrompt: Promise<void> | null = null;

const promptAndRegister = async (client: StreamChat) => {
  const permission = await requestChatPushPermission();
  // Stored BEFORE registering on purpose: the user's answer is final regardless of whether this
  // launch manages to reach APNs/FCM/Stream, and a stored "on" makes the next launch retry silently.
  setChatPushPreference(permission === "granted" ? "on" : "off");
  if (permission === "granted") {
    await registerChatPushDevice(client);
  }
};

/**
 * The one place the system permission prompt fires: the first time a persona reaches the chat
 * list on this device (i.e. after `ChatOnboardingGate` passed). Asking earlier — at login — would make
 * people decline before they know chat exists, and neither iOS nor Android re-prompts. The answer
 * is stored as the device preference; a grant registers immediately with the client we already
 * hold, later launches go through `useChatPushRegistration`.
 */
export const useChatPushFirstPrompt = (client: StreamChat | undefined) => {
  useEffect(() => {
    if (!client || !isNativePlatform() || getChatPushPreference() !== null) {
      return;
    }

    if (inFlightPrompt) {
      return;
    }
    inFlightPrompt = promptAndRegister(client)
      .catch((error) => {
        Sentry.captureException(error, { tags: { feature: "chatPush" } });
      })
      .finally(() => {
        inFlightPrompt = null;
      });
  }, [client]);
};
